import { IApp03Sprint, IEntityChildren } from '../types';
import { AppGlobals } from '../AppGlobals';
import { EntityService } from './EntityService';
import { App03SprintStatus } from '../types/App03Sprint';

export class App03SprintService extends EntityService<IApp03Sprint> {
    private apiURL: string;
    protected childrenEntities = ['CustomFields($expand=CustomFieldDfn)'];

    constructor() {
        super(`/App03Sprints`);
        const apiURL = process.env.REACT_APP_ZedX_API!;
        this.apiURL = apiURL;
    }

    public async closeSprint(payload: {
        SprintID: number;
        PlannedSprintID?: number | null;
        DecisionKey: number;
        TaskIDs?: string; // comma-separated string of task IDs
    }): Promise<void> {
        await this.axiosPost(`${this.apiURL}/CloseSprint`, payload);
    }


    public readAllForList(workflowID?: number): Promise<IApp03Sprint[]> {
        return this.readAll(
            `?$select=ID,Title,SprintStatus,StartDate,EndDate,VelocityTarget,ActualVelocity,SprintNumber,WorkflowID`
            + `&$orderby=ID`
            + `&$filter=CustomerID eq ${AppGlobals.CustomerID}`
            + (workflowID ? ` and WorkflowID eq ${workflowID}` : '')
        );
    }

    public readAllPlannedSprints(workflowID: number): Promise<IApp03Sprint[]> {
        return this.readAll(
            `?$select=ID,Title,SprintNumber`
            + `&$orderby=SprintNumber`
            + `&$filter=CustomerID eq ${AppGlobals.CustomerID} and WorkflowID eq ${workflowID} and SprintStatus eq '${App03SprintStatus.Planned}'`
        );
    }

    public countAllForList(): Promise<IApp03Sprint[]> {
        return this.readAll(
            `?$select=ID`
            + `&$orderby=Title`
            + `&$filter=CustomerID eq ${AppGlobals.CustomerID}`
        );
    }

    public readAllForLookup(includeClosedEntities?: boolean, additionalFields?: string): Promise<IApp03Sprint[]> {
        return this.readAll(
            `?$select=ID,Title${additionalFields ? `,${additionalFields}` : ''}`
            + `&$orderby=Title&$filter=CustomerID eq ${AppGlobals.CustomerID}`
        );
    }

    public async readAllForAddTaskDropdown(workflowID: number): Promise<IApp03Sprint[]> {
        const baseFilter = `CustomerID eq ${AppGlobals.CustomerID} and WorkflowID eq ${workflowID}`;

        const [plannedActive, completed] = await Promise.all([
            this.readAll(
                `?$select=ID,Title,SprintStatus,SprintNumber`
                + `&$orderby=SprintNumber`
                + `&$filter=(${baseFilter}) and (SprintStatus eq '${App03SprintStatus.Planned}' or SprintStatus eq '${App03SprintStatus.Active}')`
            ),
            this.readAll(
                `?$select=ID,Title,SprintStatus,SprintNumber`
                + `&$orderby=SprintNumber desc&$top=3`
                + `&$filter=(${baseFilter}) and SprintStatus eq '${App03SprintStatus.Completed}'`
            ),
        ]);

        return [...completed.reverse(), ...plannedActive];
    }

    public async readLastCurrentAndPlannedSprints(workflowID: number): Promise<IApp03Sprint[]> {
        const baseFilter = `CustomerID eq ${AppGlobals.CustomerID} and WorkflowID eq ${workflowID}`;

        const [lastCompleted, current, planned] = await Promise.all([
            // a) Last one completed sprint
            this.readAll(
                `?$select=ID,Title,SprintStatus,SprintNumber`
                + `&$orderby=SprintNumber desc&$top=1`
                + `&$filter=(${baseFilter}) and SprintStatus eq '${App03SprintStatus.Completed}'`
            ),
            // b) Current (Active) sprint
            this.readAll(
                `?$select=ID,Title,SprintStatus,SprintNumber`
                + `&$filter=(${baseFilter}) and SprintStatus eq '${App03SprintStatus.Active}'`
            ),
            // c) All Planned sprints
            this.readAll(
                `?$select=ID,Title,SprintStatus,SprintNumber`
                + `&$orderby=SprintNumber`
                + `&$filter=(${baseFilter}) and SprintStatus eq '${App03SprintStatus.Planned}'`
            ),
        ]);

        // Combine in order: last completed → current → planned
        return [...lastCompleted, ...current, ...planned];
    }

    public async readAllSprints(workflowID: number): Promise<IApp03Sprint[]> {
        const baseFilter = `CustomerID eq ${AppGlobals.CustomerID} and WorkflowID eq ${workflowID}`;

        return await this.readAll(
            `?$select=ID,Title,SprintStatus,SprintNumber`
            + `&$orderby=SprintNumber`
            + `&$filter=(${baseFilter})`
        );
    }

    public async entityChildren(id: number): Promise<IEntityChildren[]> {
        const entityUrl = `${this.entityUrl}(${id})`;
        const tasks = (await this.axiosGet(`${entityUrl}/App03Tasks?$select=ID&$top=10`)).data.value;
        const evidences = (await this.axiosGet(`${entityUrl}/App03SprintEvidences?$select=ID&$top=10`)).data.value;
        return [
            { ChildType: 'Tasks', CanBeAdopted: false, ChildIDs: (await tasks).map((m: { ID: any }) => m.ID) },
            { ChildType: 'Comments and File Attachments', CanBeAdopted: false, ChildIDs: (await evidences).map((w: { ID: any }) => w.ID) }
        ];
    }

}